import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import {
  Loading,
  Grid,
  Badge,
  Wrap,
  Box,
  Stack,
  Heading,
  Text,
  Button,
  Input,
  SimpleModal,
  ConfirmButton,
  EmptyBox,
  ReadOnlyCopyInput
} from '@/components/lib'
import api from '../../api'
import useStore from '../../store'
import utils from '../../utils/utils'
import NewApiKey, { describeScopes, isReadOnly } from './NewApiKey'

export default function AccountSettings () {
  const [tokens, setTokens] = useState([])
  const [newToken, setNewToken] = useState()
  const [loadingTokens, setLoadingTokens] = useState(false)
  const [connections, setConnections] = useState([])
  const [loadingConnections, setLoadingConnections] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const navigate = useNavigate()
  const { team, completeLogout } = useStore()

  useEffect(() => {
    setLoadingTokens(true)
    api.accounts.getApiTokens(({ tokens }) => {
      setTokens(tokens)
      setLoadingTokens(false)
    })
    setLoadingConnections(true)
    api.oauth.listConnections(({ connections }) => {
      setConnections(connections)
      setLoadingConnections(false)
    }, () => setLoadingConnections(false))
  }, [])

  const revokeConnection = (connection) => {
    api.oauth.revokeConnection(connection.clientId, () => {
      setConnections(connections.filter(c => c.clientId !== connection.clientId))
      utils.toast('success', `${connection.clientName} disconnected`)
    }, err => {
      utils.toast('error', 'Unable to disconnect this application', err.message)
    })
  }

  // The key's one readable moment: NewApiKey has created it, and the dialog
  // below is the only place it is ever shown.
  const onKeyCreated = (token) => {
    utils.toast(
      'success',
      'Token created',
      "We've created that access key for you."
    )
    setTokens([...tokens, token])
    setNewToken(token)
  }

  const deleteApiToken = (token) => {
    api.accounts.deleteApiToken(
      token.id,
      () => {
        utils.toast(
          'success',
          'Token deleted',
          "We've deleted that access key for you."
        )
        setTokens(tokens.filter((t) => t.id !== token.id))
      },
      (err) => {
        utils.toast('error', 'Unable to delete your token', err.message)
      }
    )
  }

  const deleteAccount = () => {
    const confirm = window.confirm('Really delete your account?')
    if (!confirm) return
    api.accounts.delete(
      deletePassword,
      () => {
        api.accounts.localLogout(() => completeLogout())
        navigate('/')
        utils.toast('info', 'Account deleted', 'Sorry to see you go.')
      },
      (err) => utils.toast('error', 'Unable to delete account', err.message)
    )
  }

  return (
    <Stack spacing={5}>
      <Stack gap={3} rounded='lg' borderWidth='1px' p={3}>
        <Box>
          <Heading as='h3' size='md'>
            API keys
          </Heading>
          <Text>
            Grant other applications access to your Trialflare account using API
            keys.
          </Text>
        </Box>

        <Box>
          <NewApiKey onCreated={onKeyCreated} />
        </Box>

        <Text>
          The following keys are currently active and have access to your
          Trialflare account.
        </Text>
        {loadingTokens && <Loading />}
        {!tokens || tokens.length === 0
          ? (
            <EmptyBox
              icon='keys'
              title='API keys will appear here once created'
              description='No API keys have been created yet.'
            />
            )
          : (
            <Grid
              templateColumns={{ md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
              gap={2}
            >
              {tokens.map((token) => (
                <Stack
                  key={token.id}
                  borderWidth='1px'
                  rounded='md'
                  width='100%'
                  p={2}
                >
                  <Heading as='h5' size='sm'>
                    {token.serviceName}
                  </Heading>
                  <Wrap gap={1}>
                    <Badge colorPalette={isReadOnly(token.scopes) ? 'blue' : 'orange'}>
                      {describeScopes(token.scopes)}
                    </Badge>
                    {token.trials &&
                      <Badge colorPalette='purple'>
                        {token.trials.length === 1
                          ? token.trials[0].name
                          : `${token.trials.length} trials`}
                      </Badge>}
                  </Wrap>
                  {token.trials && token.trials.length > 1 &&
                    <Text fontSize='xs' color='fg.muted'>
                      {token.trials.map(t => t.name).join(', ')}
                    </Text>}
                  {token.expiresAt
                    ? (
                      <Text
                        fontSize='xs'
                        color={
                      new Date(token.expiresAt) < new Date() ? 'red' : 'green'
                    }
                      >
                        Expires: {utils.formatDateTime(token.expiresAt)}
                      </Text>
                      )
                    : (
                      <Text fontSize='xs'>No expiry</Text>
                      )}
                  <ConfirmButton
                    trigger={
                      <Button size='xs' variant='outline' colorPalette='red'>
                        Delete
                      </Button>
                  }
                    header='Really delete this API token?'
                    footer='Other services using this token will lose access to your Trialflare account.'
                    onConfirm={(e) => deleteApiToken(token)}
                    actionColor='red'
                  />
                </Stack>
              ))}
            </Grid>
            )}
      </Stack>

      <Stack gap={3} rounded='lg' borderWidth='1px' p={3}>
        <Box>
          <Heading as='h3' size='md'>Connected applications</Heading>
          <Text>
            External applications you have granted access to your Trialflare account,
            such as an AI assistant. Each can only see what your own permissions allow.
          </Text>
        </Box>
        {loadingConnections && <Loading />}
        {!loadingConnections && connections.length === 0
          ? (
            <EmptyBox
              icon='keys'
              title='No connected applications'
              description='Applications you connect will appear here.'
            />)
          : (
            <Grid templateColumns={{ md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={2}>
              {connections.map(connection => (
                <Stack key={connection.clientId} borderWidth='1px' rounded='md' width='100%' p={2}>
                  <Heading as='h5' size='sm'>{connection.clientName}</Heading>
                  <Text fontSize='xs'>
                    Connected: {utils.formatDateTime(connection.connectedAt)}
                  </Text>
                  <Text fontSize='xs'>
                    {connection.lastUsedAt
                      ? `Last used: ${utils.formatDateTime(connection.lastUsedAt)}`
                      : 'Not used yet'}
                  </Text>
                  <ConfirmButton
                    trigger={
                      <Button size='xs' variant='outline' colorPalette='red'>
                        Disconnect
                      </Button>
                    }
                    header='Really disconnect this application?'
                    footer='It will immediately lose access to your Trialflare account and will need to be reconnected.'
                    onConfirm={() => revokeConnection(connection)}
                    actionColor='red'
                  />
                </Stack>
              ))}
            </Grid>)}
      </Stack>

      <Stack gap={3} rounded='lg' borderWidth='1px' p={3}>
        <Box>
          <Heading as='h3' size='md'>
            Data protection complaints
          </Heading>
          <Text>
            Trialflare has a dedicated procedure for managing data protection
            complaints.
          </Text>
        </Box>
        <Box>
          <Button
            as='a'
            target='_blank'
            href={`${import.meta.env.VITE_HELP_CENTRE_HOST}/data_protection_security/#data-protection-complaints`}
          >
            Make a complaint
          </Button>
        </Box>
      </Stack>

      <Stack gap={3} rounded='lg' borderWidth='1px' p={3}>
        <Box>
          <Heading as='h3' size='md'>
            Delete account
          </Heading>
          <Text>
            This will immediately disassociate your user account from{' '}
            {team && team.name}'s team account and will then fully delete your
            user account. {team && team.name}'s team account and any trials
            within it will not be affected. This action cannot be un-done.
          </Text>
        </Box>
        <Text fontSize='lg'>
          To continue, please enter your Trialflare account password. (Note: if
          you only login via single sign-on, you can leave your password blank).
        </Text>

        <Input
          placeholder='Your account password'
          type='password'
          value={deletePassword}
          onChange={(e) => setDeletePassword(e.target.value)}
        />
        <Stack direction='row' justifyContent='end'>
          <Button colorPalette='red' onClick={deleteAccount}>
            Delete account
          </Button>
        </Stack>
      </Stack>

      <SimpleModal
        isOpen={newToken}
        onClose={() => setNewToken()}
        header='Your new Trialflare API key'
        body={
          <Stack>
            <Text>Your new key is displayed below.</Text>
            <Text fontWeight='bold'>
              Important: This is the only time it will be shown to you so please
              make sure you have securely stored it before closing this message.
            </Text>
            <ReadOnlyCopyInput value={newToken?.token} />
          </Stack>
        }
        footer={
          <Button colorPalette='primary' onClick={() => setNewToken()}>
            Close message
          </Button>
        }
      />
    </Stack>
  )
}
